// @ts-strict-ignore
import {PropertyFilter} from 'common/widgets/property-filter-menu/models/property-filter';
import {PropertyFilterFactory} from 'common/widgets/property-filter-menu/models/utils';
import {DisplayContext as PropDisplayContext} from 'common/widgets/property-filter-menu/models/types';
import {ChartType, PropertyType, ReportName, ResourceType} from 'common/report/constants';
import {
  AGGREGATE_USER_FREQ_BY_GROUP,
  BEHAVIOR_AGGREGATION,
  BEHAVIOR_FREQUENCY,
  FUNNELS_TIME_TO_CONVERSION,
  RECURRING_REVENUE_CHANGE,
  RECURRING_REVENUE_CHANGE_TYPE,
} from 'common/query-builder/query-block/properties-menu/constants';
import {MenuItemType, SelectedPropertyItem} from 'common/query-builder/query-block/properties-menu/types';
import {Segmentation, WindowLengthUnit, WindowType} from 'common/types/reports/query';
import {SegmentValue} from 'common/query-builder/query-block/segment-menu/types';
import {PropertySectionItem} from 'common/query-builder/query-block/properties-menu/item-menu-types';
import {propertySelectionToSegmentation} from 'common/query-builder/query-block/segment-menu/util';
import {TimeUnit} from 'common/widgets/date-range-picker/models/types';
import {isScdPropertyName} from 'common/report/util/properties';
import {isTimeSeriesChartType} from 'common/report/insights/visualization/charts/chart-display/utils';
import {NumberOperator} from 'common/query-builder/cohorts-query-builder/models/enums/builder-enums';

import {
  DEFAULT_AGGREGATE_USER_FREQ_BY_GROUP_BEHAVIOR,
  DEFAULT_AGGREGATION_BEHAVIOR,
  DEFAULT_BEHAVIORLESS_SEGMENTATION,
  DEFAULT_FREQUENCY_BEHAVIOR,
  DEFAULT_MULTI_ATTRIBUTION_BEHAVIOR,
  DEFAULT_REVENUE_CHANGE_BEHAVIOR,
  DEFAULT_REVENUE_CHANGE_TYPE_BEHAVIOR,
  getDefaultPropertyValueForBehavior,
  isMultiAttributionSegmentation,
} from '../row-types/behavior/utils';

/**
 * Populates the right `Segmentation` format based on items selected from `mp-properties-menu`
 */
export function getPropertySelectionFromProperty(
  selectedItem: SelectedPropertyItem,
  currentSelection: Segmentation,
  {
    isForFilter,
    reportName,
    dataGroupId,
    chartType,
    dateUnit,
  }: {
    isForFilter?: boolean;
    reportName?: ReportName;
    dataGroupId?: string;
    chartType?: ChartType;
    dateUnit?: string;
  } = {},
): SegmentValue | Partial<PropertyFilter> {
  const {menuItemType, item, stagedItemsStack} = selectedItem;
  const isMultiAttributionBehavior = stagedItemsStack?.some(
    (stagedItem) => stagedItem.menuItemType === MenuItemType.BehaviorMultiAttribution,
  );
  let selection: SegmentValue | Partial<PropertyFilter>;
  const isTimeToConvertSegmentation = item[`value`] === FUNNELS_TIME_TO_CONVERSION.value;
  const isTimeToConvertFilter = item[`propertyName`] === FUNNELS_TIME_TO_CONVERSION.value;

  if (menuItemType === MenuItemType.AggregateUserByGroup) {
    selection = {
      ...DEFAULT_BEHAVIORLESS_SEGMENTATION,
      propertyType: PropertyType.Number,
      propertyName: AGGREGATE_USER_FREQ_BY_GROUP.value,
      propertyDefaultType: PropertyType.Number,
      behavior: {
        ...DEFAULT_AGGREGATE_USER_FREQ_BY_GROUP_BEHAVIOR,
      },
    };
    if (isForFilter) {
      selection.filterOperator = NumberOperator.AtLeast;
    }
  } else if (menuItemType === MenuItemType.BehaviorWithProperty) {
    const isRevenueChange = [item[`value`], item[`propertyName`]].includes(RECURRING_REVENUE_CHANGE.value);
    selection = {
      ...DEFAULT_BEHAVIORLESS_SEGMENTATION,
      propertyName: isRevenueChange ? RECURRING_REVENUE_CHANGE.value : RECURRING_REVENUE_CHANGE_TYPE.value,
      propertyType: isRevenueChange ? RECURRING_REVENUE_CHANGE.type : RECURRING_REVENUE_CHANGE_TYPE.type,
      propertyDefaultType: isRevenueChange ? RECURRING_REVENUE_CHANGE.type : RECURRING_REVENUE_CHANGE_TYPE.type,
      behavior: {
        ...(isRevenueChange ? DEFAULT_REVENUE_CHANGE_BEHAVIOR : DEFAULT_REVENUE_CHANGE_TYPE_BEHAVIOR),
        offset: {
          value: isTimeSeriesChartType(chartType) ? -1 : `$start`,
          // tmp hack until we fix dateUnit type on qes
          unit: dateUnit as TimeUnit,
        },
        property: {
          resourceType: ResourceType.User,
          dataGroupId,
          propertyDefaultType: PropertyType.String,
          type: PropertyType.String,
          label: null,
          value: null,
        },
      },
    };
  } else if (!stagedItemsStack?.length && menuItemType === MenuItemType.BehaviorWithEvent) {
    const isBehavior =
      [BEHAVIOR_FREQUENCY.value, BEHAVIOR_AGGREGATION.value].includes(item[`value`]) ||
      [BEHAVIOR_FREQUENCY.value, BEHAVIOR_AGGREGATION.value].includes(item[`propertyName`]);
    const isAggregation =
      item[`value`] === BEHAVIOR_AGGREGATION.value || item[`propertyName`] === BEHAVIOR_AGGREGATION.value;
    const shouldSetStepNumbers = isBehavior && reportName === ReportName.Funnels;
    const shouldSetWindowDuration = isBehavior && reportName === ReportName.Retention;
    selection = {
      ...DEFAULT_BEHAVIORLESS_SEGMENTATION,
      behavior: {
        ...(isAggregation ? DEFAULT_AGGREGATION_BEHAVIOR : DEFAULT_FREQUENCY_BEHAVIOR),
        ...(shouldSetStepNumbers
          ? {
              startStepNumber: 0,
              endStepNumber: 1,
            }
          : {}),
        ...(shouldSetWindowDuration
          ? {
              windowType: WindowType.AFTER,
              windowLength: 7,
              windowLengthUnit: WindowLengthUnit.DAY,
            }
          : {}),
        event: {custom: false, label: ``, value: null},
        property: getDefaultPropertyValueForBehavior(isAggregation),
      },
    };
  } else if (isMultiAttributionBehavior) {
    selection = {
      ...propertySelectionToSegmentation(item),
      behavior: {
        ...(isMultiAttributionSegmentation(currentSelection)
          ? // keep any existing behavior selection
            currentSelection.behavior
          : DEFAULT_MULTI_ATTRIBUTION_BEHAVIOR),
        property: item as PropertySectionItem,
      },
    };
  } else if (isForFilter) {
    selection = PropertyFilterFactory.createPropertyFilter(item as Partial<PropertyFilter>, {
      displayContext: PropDisplayContext.Default,
    });

    // this is a bit weird:
    // property menu does not hold any state for special proper configuration.
    // so it's this function's responsibility to set the correct scdOptions
    if (
      !Array.isArray(item) &&
      isScdPropertyName((item as Partial<PropertyFilter>)?.propertyName) &&
      isScdPropertyName(currentSelection?.propertyName)
    ) {
      selection = {
        ...selection,
        scdOptions: {
          ...(item as Partial<PropertyFilter>)?.scdOptions,
          ...currentSelection.scdOptions,
        },
      };
    }
    if (isTimeToConvertFilter) {
      selection = {
        ...selection,
        startStepNumber: null, // Start selection with "All Steps"
        endStepNumber: null,
      };
    }
  } else if (isTimeToConvertSegmentation) {
    selection = {
      ...propertySelectionToSegmentation(item),
      startStepNumber: null, // Start selection with "All Steps"
      endStepNumber: null,
    };
  } else {
    selection = propertySelectionToSegmentation(item);
  }

  return selection;
}
